import type { TableRowCellConfigExtraDataType, TableRowType } from '../components/editor/grid/reduxStore/table.types';

export const CELL_BACKGROUND_COLOR_VARIABLE = '--py-cell-background-color';

export const CELL_BORDER_SIDES = ['Top', 'Right', 'Bottom', 'Left'] as const;

export type CellBorderSide = (typeof CELL_BORDER_SIDES)[number];

export const getCellBorderVariable = (side: CellBorderSide) => `--py-cell-border-${side.toLowerCase()}`;

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const isHexColor = (value: unknown): value is string => typeof value === 'string' && HEX_COLOR_PATTERN.test(value);

const isBorderWidth = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;

// Escapes a value for a double-quoted CSS attribute selector string, including control characters such as newlines,
// which would otherwise end the string and make the browser drop the rule.
export const escapeAttributeValue = (value: string) =>
  value
    .replace(/["\\]/g, '\\$&')
    // eslint-disable-next-line no-control-regex -- matching control characters is the point
    .replace(/[\u0000-\u001f\u007f]/g, (character) => `\\${character.charCodeAt(0).toString(16)} `);

const getCellStyleVariables = (styles: TableRowCellConfigExtraDataType['styles']) => {
  const variables: Record<string, string> = {};
  if (!styles) return variables;

  if (isHexColor(styles.backgroundColor)) {
    variables[CELL_BACKGROUND_COLOR_VARIABLE] = styles.backgroundColor;
  }

  CELL_BORDER_SIDES.forEach((side) => {
    const width = styles[`border${side}Width`];
    const color = styles[`border${side}Color`];
    if (isBorderWidth(width) && isHexColor(color)) {
      variables[getCellBorderVariable(side)] = `${width}px solid ${color}`;
    }
  });

  return variables;
};

// Scopes each cell's styles to one table by setting CSS variables that the theme's cell rules read, so editor states
// (hidden cell, column selection, focus, selected row) keep precedence over the cell's own background and borders.
export const getCellStyles = (rows: Pick<TableRowType, 'id' | 'cellConfig'>[], scopeSelector: string) => {
  const rules: Record<string, Record<string, string>> = {};

  rows.forEach((row) => {
    Object.entries(row.cellConfig ?? {}).forEach(([field, config]) => {
      const variables = getCellStyleVariables(config?.styles);
      if (Object.keys(variables).length === 0) return;

      const rowSelector = `.MuiDataGrid-row[data-id="${escapeAttributeValue(String(row.id))}"]`;
      const cellSelector = `.MuiDataGrid-cell[data-field="${escapeAttributeValue(field)}"]`;
      rules[`${scopeSelector} ${rowSelector} ${cellSelector}`] = variables;
    });
  });

  return rules;
};
